import {
  Callout,
  Collapse,
  Intent,
  Label,
  PanelProps,
  Position,
} from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { noop } from 'vet-bones/utils';

import { DatabaseSelector } from 'src/components/DatabaseSelector';
import { InlineEditableLabel } from 'src/components/InlineEditableLabel';
import { TEST_IDS } from 'src/constants/testIds';
import { useAddUploadModel } from 'src/containers/ProjectModelDialog/hooks/useAddUploadModel';
import { useUploadModel } from 'src/containers/ProjectModelDialog/hooks/useUploadModelValue';
import * as copy from 'src/templates/copy';
import { checkImportModelLimits } from 'src/utils/checkImportModelLimits';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export type UploadModelPanelProps = {
  file: File;
};

export const UploadModelPanel: React.VFC<PanelProps<UploadModelPanelProps>> = ({
  file,
}) => {
  const { nameValidationError, namedGraphValidationError } =
    useAddUploadModel();
  const {
    databaseId,
    isModelDataLoading,
    isNameEdited,
    isNamedGraphIriEdited,
    name,
    namedGraphIri,
    pendingModelData,
    setDatabaseId,
    setIsModelDataLoading,
    setName,
    setNamedGraphIri,
    setPendingModelData,
  } = useUploadModel();

  const pendingAddModelToDb = useReduxSelector(
    (state) => state.databases.pendingAddModelToDatabase
  );

  const isOverLimit = React.useMemo(
    () =>
      pendingModelData
        ? checkImportModelLimits({
            [pendingModelData.modelName]: pendingModelData,
          })
        : false,
    [pendingModelData]
  );

  React.useEffect(() => {
    setPendingModelData(null);
    setIsModelDataLoading(false);
  }, [databaseId, setPendingModelData, setIsModelDataLoading]);

  const hasPendingModelData = Boolean(pendingModelData);

  return (
    <div className="sd-model-upload-panel">
      <Collapse
        className={classNames('sd-model-upload-panel-warning', {
          'sd-model-upload-panel-warning__open': hasPendingModelData,
        })}
        isOpen={Boolean(isOverLimit)}
      >
        <Callout
          data-testid={TEST_IDS.projectModelDialog.validation.largeModelLimit}
          intent={Intent.WARNING}
        >
          {copy.components.projectModelDialog.validation.largeModelLimit}
        </Callout>
      </Collapse>
      <InlineEditableLabel
        disabled
        label={copy.components.projectModelDialog.uploadModelPanel.fileUpload}
        monospaceText={false}
        onSetValue={noop}
        testId={TEST_IDS.projectModelDialog.uploadModelPanel.fileUploadInput}
        value={file.name}
      />
      <Label>
        {
          copy.components.publishProjectDrawer.databaseConfiguration
            .databaseSectionHeader
        }
        <div className="sd-drawer-selector-menu">
          <DatabaseSelector
            databaseId={databaseId}
            disabled={
              hasPendingModelData || isModelDataLoading || pendingAddModelToDb
            }
            popoverPosition={Position.BOTTOM_LEFT}
            setDatabaseId={setDatabaseId}
          />
        </div>
      </Label>
      <InlineEditableLabel
        disabled={
          hasPendingModelData || isModelDataLoading || pendingAddModelToDb
        }
        label={copy.components.projectModelDialog.uploadModelPanel.name}
        monospaceText={false}
        onBlur={() => {
          if (!name) {
            setName(
              copy.components.projectModelDialog.uploadModelPanel
                .nameExampleText
            );
          }
        }}
        onSetValue={setName}
        // use calculated value
        onValidate={() =>
          hasPendingModelData ||
          isModelDataLoading ||
          pendingAddModelToDb ||
          !isNameEdited ||
          !nameValidationError
            ? ''
            : nameValidationError
        }
        placeholder=""
        testId={TEST_IDS.projectModelDialog.uploadModelPanel.nameInput}
        value={name}
      />
      <InlineEditableLabel
        disabled={
          hasPendingModelData || isModelDataLoading || pendingAddModelToDb
        }
        label={copy.components.projectModelDialog.uploadModelPanel.namedGraph}
        onSetValue={setNamedGraphIri}
        // use calculated value
        onValidate={() =>
          hasPendingModelData ||
          isModelDataLoading ||
          pendingAddModelToDb ||
          !isNamedGraphIriEdited
            ? ''
            : namedGraphValidationError
        }
        placeholder=""
        testId={TEST_IDS.projectModelDialog.uploadModelPanel.namedGraphInput}
        value={namedGraphIri}
      />
    </div>
  );
};
